import { callAllHandlers } from "@smartsheet/lodestar-utils";
import { MouseEventHandler, useMemo, useState } from "react";

export interface UseHoveredOptions<E> {
    /**
     * A custom handler for this event that will be folded into the composite handlers
     * that are returned from this hook. This function is required for this hook to work,
     * so to make sure your function doesn't get overridden by this one, please pass it
     * along here.
     */
    onMouseEnter?: MouseEventHandler<E>;
    /**
     * A custom handler for this event that will be folded into the composite handlers
     * that are returned from this hook. This function is required for this hook to work,
     * so to make sure your function doesn't get overridden by this one, please pass it
     * along here.
     */
    onMouseLeave?: MouseEventHandler<E>;
}

export interface UseHoveredResult<E> {
    /**
     * If the user's mouse is over the element, this will be true.
     */
    isHovered: boolean;
    /**
     * The resulting handler to be applied to the target element.
     * If you passed a customization in, it will be called first before this hook
     * calls its own.
     */
    onMouseEnter: MouseEventHandler<E>;
    /**
     * The resulting handler to be applied to the target element.
     * If you passed a customization in, it will be called first before this hook
     * calls its own.
     */
    onMouseLeave: MouseEventHandler<E>;
}

export const useHovered = <TargetElementType extends HTMLElement = HTMLDivElement>({
    onMouseEnter: providedOnMouseEnter,
    onMouseLeave: providedOnMouseLeave,
}: UseHoveredOptions<TargetElementType> = {}) => {
    const [isHovered, setIsHovered] = useState(false);

    const onMouseEnter: MouseEventHandler<TargetElementType> = useMemo(
        () =>
            callAllHandlers(providedOnMouseEnter, () => {
                setIsHovered(true);
            }),
        [providedOnMouseEnter]
    );

    const onMouseLeave: MouseEventHandler<TargetElementType> = useMemo(
        () =>
            callAllHandlers(providedOnMouseLeave, () => {
                setIsHovered(false);
            }),
        [providedOnMouseLeave]
    );

    const result: UseHoveredResult<TargetElementType> = {
        isHovered,
        onMouseEnter,
        onMouseLeave,
    };

    return result;
};
